import React, { useEffect, useMemo, useState } from 'react';

const canUseDesktopPicker = () => typeof window.workloopRealm?.listShareSources === 'function';

const SourceCard = ({ source, onPick }) => (
  <button
    type="button"
    onClick={() => onPick(source)}
    className="group flex flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 text-left hover:border-sky-400 hover:bg-white/10"
  >
    <div className="aspect-video bg-black/60 overflow-hidden">
      {source.thumbnail ? (
        <img src={source.thumbnail} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-[11px] text-white/40">
          No preview
        </div>
      )}
    </div>
    <div className="flex items-center gap-2 px-2.5 py-2">
      {source.appIcon ? (
        <img src={source.appIcon} alt="" className="h-4 w-4 rounded-sm shrink-0" />
      ) : null}
      <span className="truncate text-[12px] font-medium text-white">{source.name}</span>
    </div>
  </button>
);

/**
 * Desktop: pick a full screen or an app window, then LiveKit publishes it.
 * Browser: native getDisplayMedia picker already offers screen vs window.
 */
const ScreenSharePicker = ({ open, onPick, onCancel }) => {
  const [tab, setTab] = useState('screen');
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    if (!canUseDesktopPicker()) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await window.workloopRealm.listShareSources();
        if (cancelled) return;
        setSources(Array.isArray(list) ? list : []);
        if (!Array.isArray(list) || list.length === 0) {
          setError('No screens or windows found. On macOS, allow Screen Recording for WorkLoop in System Settings.');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Could not list screens');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  const visible = useMemo(
    () => sources.filter((s) => (tab === 'screen' ? s.kind === 'screen' : s.kind === 'window')),
    [sources, tab],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Share screen"
        className="w-full max-w-3xl rounded-2xl border border-white/15 bg-gray-950 text-white shadow-2xl"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <p className="text-sm font-semibold">Share screen</p>
          <button
            type="button"
            onClick={onCancel}
            className="px-2 py-1 text-xs rounded-lg bg-white/10 hover:bg-white/20"
          >
            Cancel
          </button>
        </div>
        <div className="px-4 pt-3 flex gap-2">
          <button
            type="button"
            onClick={() => setTab('screen')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
              tab === 'screen' ? 'bg-sky-500 text-black' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            Full screen
          </button>
          <button
            type="button"
            onClick={() => setTab('window')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
              tab === 'window' ? 'bg-sky-500 text-black' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            App window
          </button>
        </div>
        <div className="p-4 min-h-[220px]">
          {loading && (
            <p className="text-sm text-white/60">Loading {tab === 'screen' ? 'screens' : 'windows'}…</p>
          )}
          {error && !loading && (
            <p className="text-sm text-red-300">{error}</p>
          )}
          {!loading && !error && visible.length === 0 && (
            <p className="text-sm text-white/60">
              {tab === 'screen' ? 'No displays found.' : 'No app windows found.'}
            </p>
          )}
          {!loading && visible.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[50vh] overflow-y-auto pr-1">
              {visible.map((source) => (
                <SourceCard key={source.id} source={source} onPick={onPick} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { canUseDesktopPicker };
export default ScreenSharePicker;
